import React, { useEffect, useState, useRef } from "react";
import "./Anonymity.scss";
import { Breadcrumb, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import EziLoader from "components/EziLoader";
import Axios from "utility/Axios";
import configURL from "config/config";
import { toast } from "react-toastify";
import DataTable from "components/DataTable";
import SweetSearch from "components/SweetSearch";
import { FaCircleInfo } from "react-icons/fa6";
import Select from 'react-select';

const Anonymity = () => {
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [usersData, setUsersData] = useState([]);
    const [dataColumn, setDataColumn] = useState([{}]);
    const [selectedReport, setSelectedReport] = useState([]);
    const [allData, setAllData] = useState([]);
    const [selectedReportID, setSelectedReportID] = useState([]);
    const [nonSelectedReportID, setNonSelectedReportID] = useState([]);
    const [currentSelected, setCurrentSelected] = useState([]);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [rounderOptions, setRounderOptions] = useState([]);
    const [aggregatorOptions, setAggregatorOptions] = useState([]);
    const [providerLoaded, setProviderLoaded] = useState(false);
    const [logicOptionsMap, setLogicOptionsMap] = useState({});
    const per_page = useRef(10);
    const inputSearch = useRef(null);
    var searchTimer = null;

    /**
     * Handle save report data to the server.
     * 
     * This function creates a formData with the selected report data and send it to the server.
     * If the server response is success, it clears the selected report data and calls getReportMapping
     * to fetch the updated report data. It also displays a toast warning with the server response message.
     * If the server response is not success, it displays a toast warning with the server response message.
     */
    const handleSaveReportData = () => {
        if (currentSelected.length === 0) {
            toast.warn("Please select at least one report type");
            return;
        }
        setLoading(true);
        let formData = new FormData();
        formData.append("report_type_json", JSON.stringify(currentSelected));
        Axios.post(configURL.manage_partner_report_types_mapping, formData).then((response) => {
            if (response.data.success === true) {
                setSelectedReportID([]);
                setSelectedReport([]);
                setCurrentSelected([]);
                getReportMapping();
                setLoading(false);
                per_page.current = 10;
                toast.success(response.data.message);
            } else {
                toast.warn(response.data.message);
                setLoading(false);
            }
        });
    };

    /**
     * Handles table changes triggered by pagination or sorting.
     *
     * @param {string} type - The type of table change event (e.g., "pagination", "sort").
     * @param {Object} props - The properties related to the table change event.
     * @param {number} [props.page] - The current page number, used when type is "pagination".
     */

    const handleTableChange = (type, props) => {
        switch (type) {
            case "pagination":
                setLoading(true);
                getReportMapping(props.page, props.sizePerPage);
                per_page.current = props.sizePerPage;
                break;
            case "sort":
                setLoading(true);
                getReportMapping();
                break;
            case "cellEdit":
                handleEditCell(props);
            default:
                break;
        }
    };

    /**
     * Updates the usersData state when a cell in the table is updated.
     * 
     * @param {any} oldValue - The old value of the cell.
     * @param {any} newValue - The new value of the cell.
     * @param {Object} row - The row data of the table.
     * @param {Object} column - The column data of the table.
     */
    const updateRowData = (oldValue, newValue, row, column) => {
        if (oldValue !== newValue) {
            row[column.dataField] = newValue;
            let objIndex = usersData.map((o) => o['report_type_id']).indexOf(row['report_type_id']);
            usersData[objIndex] = row;
            usersData[objIndex]['master_configuration_json'] = { ...usersData[objIndex]['master_configuration_json'], [column.dataField]: newValue };
            setUsersData(usersData);
        }
    }

    /**
     * Handles checkbox selection of report types.
     * 
     * This function keeps track of the selected report types by updating the selectedReportID and selectedReport state variables.
     * If the report type is selected, it adds the report type id to selectedReportID and the report type data to selectedReport.
     * If the report type is deselected, it removes the report type id from selectedReportID and the report type data from selectedReport.
     * 
     * @param {Object} row - The row data of the table.
     * @param {boolean} isSelect - Whether the report type is selected or deselected.
     */
    const handleCheckData = (row, isSelect) => {
        const ReportSelected = [...currentSelected];
        if (isSelect) {
            let index = allData.findIndex(item => item['report_type_id'] === row['report_type_id']);
            const baseConfig = allData[index]['master_configuration_json'] || {};
            ReportSelected.push({
                report_type_id: row['report_type_id'],
                master_configuration_json: {
                    ...baseConfig,
                    default_logic: baseConfig.default_logic || row.default_logic || null
                }
            });

        }
        if (!isSelect) {
            ReportSelected.splice(ReportSelected.findIndex(item => item['report_type_id'] === row['report_type_id']), 1);
        }
        setCurrentSelected(ReportSelected);
    }

    /**
     * Handles the selection of all rows in the table.
     * 
     * @param {boolean} isSelected - Whether all rows are selected or deselected.
     * @param {Array} rows - The array of row data.
     */
    const handleEditCell = (row) => {
        let modifiedData = currentSelected.map(item => {
            if (item.report_type_id === row.cellEdit.rowId) {
                return { report_type_id: item['report_type_id'], master_configuration_json: { ...item['master_configuration_json'], [row.cellEdit.dataField]: row.cellEdit.newValue } };
            }
            return item;
        });
        setCurrentSelected(modifiedData);
    }

    /**
     * Editor component for input field.
     * 
     * This component is used as custom editor for react-bootstrap-table.
     * It renders an input field and handles the change event of input field.
     * It also handles the min and max value of the input field.
     * The value of input field is delayed updated by 500ms.
     * 
     * @param {Object} editorProps - The props of react-bootstrap-table editor.
     * @param {number} value - The current value of input field.
     * @param {Object} range - The range of input field.
     * @returns {ReactElement} The custom editor component.
     */
    const InputEditor = ({ editorProps, value, range, defaulfValue, disabled }) => {
        const [currentValue, setCurrentValue] = useState(value);
        const [timeoutId, setTimeoutId] = useState(null);

        const handleChange = (e) => {
            if (disabled) return;
            if (range?.min > e?.target?.value || range?.max < e?.target?.value) {
                setLoading(false);
                toast.warn(range.max ? 'Value should be between ' + range.min + ' to ' + range.max : 'Value should be positive');
                return;
            }
            const newValue = e?.target?.value
            setCurrentValue(newValue);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            const newTimeoutId = setTimeout(() => {
                editorProps.onUpdate(defaulfValue ? newValue || 0 : newValue);
            }, 500);
            setTimeoutId(newTimeoutId);
        };
        return (
            <input
                {...editorProps}
                type="number"
                disabled={disabled}
                value={currentValue}
                onChange={handleChange}
                min={range?.min || null}
                max={range?.max || null}
            />
        );
    };
    const isRowSelected = (row) => {
        if (!row) return false;
        return currentSelected.some(
            item => item.report_type_id === row.report_type_id
        );
    };


    /**
     * Retrieves the report types and its configurations from the server and sets them in the component state.
     * 
     * @param {number} page - The page number.
     * @returns {void} This function does not return any value.
     */
    const getReportMapping = (page = 1, perpage = 10) => {
        let formData = new FormData();
        let userSearch = inputSearch.current.value;
        formData.append("page", page);
        formData.append("per_page", perpage);
        formData.append("search", userSearch);
        Axios.post(configURL.get_partner_report_types_mapping, formData).then((response) => {
            if (response.data.success === true) {
                const logicMap = {};
                let tableData = response.data.data.map((item) => {
                    const name = item.name;
                    const report_type_id = item.report_type_id;
                    const master_configuration_json = item.master_configuration_json || {};
                    // 🔹 Build options for this row
                    const logicKeys = item.logic_keys || [];
                    logicMap[report_type_id] = logicKeys.map(k => ({
                        label: k.toUpperCase(),
                        value: k
                    }));
                    return {
                        ...master_configuration_json,
                        name,
                        report_type_id,
                        master_configuration_json,
                        default_logic: master_configuration_json.default_logic || null
                    };
                });
                setLogicOptionsMap(logicMap);
                setSelectedReportID([...selectedReportID, ...response.data.data.filter((item) => item.is_partner_report_type === true).map((item) => item.report_type_id)]);
                setNonSelectedReportID(response.data.data.filter((item) => item.is_partner_report_type === true).map((item) => item.report_type_id));
                setSelectedReport(response.data.data.filter((item) => item.is_partner_report_type === true).map((item) => ({ report_type_id: item['report_type_id'], master_configuration_json: item['master_configuration_json'] })));
                setUsersData(tableData);
                setPagination(response.data.pagination);
                setAllData(response.data.data);
                setLoading(false);
                setSearchLoading(false);
            } else {
                toast.warn(response.data.message);
                setLoading(false);
                setSearchLoading(false);
            }

        });
    };

    const buildColumns = () => {
        const Data = [
            { dataField: "name", text: "Report Type" },
            { dataField: "title", text: "Display Title" },
            { dataField: "anonymity_value", text: "Anonymity" },
            { dataField: "manager_anonymity_value", text: "Manager anonymity" },
            { dataField: "tpi_anonymity_value", text: "Third party anonymity" },
            { dataField: "round_decimal_point_100_scale", text: "Round decimal(100 scale)" },
            { dataField: "round_decimal_point_5_scale", text: "Round decimal(5 scale)" },
            { dataField: "aggregator_5_scale", text: "Aggregator (5 Scale)" },
            { dataField: "rounder_5_scale", text: "Rounder (5 Scale)" },
            { dataField: "default_logic", text: "Default Logic" },
            { dataField: "aggregator_100_scale", text: "Aggregator (100 Scale)" },
            { dataField: "rounder_100_scale", text: "Rounder (100 Scale)" }
        ];

        return Data.map((item) => {
            switch (item.dataField) {
                case "name":
                    return {
                        ...item,
                        editable: false
                    }
                case "title":
                    return {
                        ...item,
                        editable: true
                    }
                case "anonymity_value":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => (
                            <InputEditor
                                editorProps={editorProps}
                                value={value}
                                range={{ min: 0 }
                                }
                                disabled={!isRowSelected(row)
                                }
                            />
                        )
                    };

                case "manager_anonymity_value":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => {
                            return <InputEditor editorProps={editorProps} value={value} range={{ min: 0 }
                            } disabled={!isRowSelected(row)
                            } />;
                        }
                    }
                case "tpi_anonymity_value":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => {
                            return <InputEditor editorProps={editorProps} value={value} range={{ min: 0 }
                            } disabled={!isRowSelected(row)
                            } />;
                        }
                    }
                case "round_decimal_point_100_scale":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => {
                            return <InputEditor defaulfValue={'0'} editorProps={editorProps} value={value || 0
                            } range={{ min: 0, max: 2 }} disabled={!isRowSelected(row)} />;
                        }
                    }
                case "round_decimal_point_5_scale":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => {
                            return <InputEditor defaulfValue={'2'} editorProps={editorProps} value={value || 0
                            } range={{ min: 0, max: 2 }} disabled={!isRowSelected(row)} />;
                        }
                    }
                case "aggregator_5_scale":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => (
                            <Select
                                isDisabled={!isRowSelected(row)
                                }
                                value={aggregatorOptions.find(opt => opt.value === value) || null}
                                options={aggregatorOptions}
                                onChange={(opt) =>
                                    editorProps.onUpdate(opt?.value || null)
                                }
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        )
                    };
                case "aggregator_100_scale":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => (
                            <Select
                                isDisabled={!isRowSelected(row)
                                }
                                value={aggregatorOptions.find(opt => opt.value === value) || null}
                                options={aggregatorOptions}
                                onChange={(opt) =>
                                    editorProps.onUpdate(opt?.value || null)
                                }
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        )
                    };
                case "rounder_5_scale":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => (
                            <Select
                                isDisabled={!isRowSelected(row)
                                }
                                value={rounderOptions.find(opt => opt.value === value) || null}
                                options={rounderOptions}
                                onChange={(opt) =>
                                    editorProps.onUpdate(opt?.value || null)
                                }
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        )
                    };
                case "default_logic":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => {
                            const options = logicOptionsMap[row.report_type_id] || [];
                            return (
                                <Select
                                    isDisabled={!isRowSelected(row)}
                                    value={options.find(opt => opt.value === value) || null}
                                    options={options}
                                    onChange={(opt) => {
                                        const newValue = opt?.value || null;
                                        editorProps.onUpdate(newValue);
                                        updateRowData(value, newValue, row, item);
                                        setCurrentSelected(prev =>
                                            prev.map(r =>
                                                r.report_type_id === row.report_type_id
                                                    ? {
                                                        ...r,
                                                        master_configuration_json: {
                                                            ...r.master_configuration_json,
                                                            default_logic: newValue
                                                        }
                                                    }
                                                    : r
                                            )
                                        );
                                    }}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            );
                        }
                    };
                case "rounder_100_scale":
                    return {
                        ...item,
                        editable: true,
                        editorRenderer: (editorProps, value, row) => (
                            <Select
                                isDisabled={!isRowSelected(row)
                                }
                                value={rounderOptions.find(opt => opt.value === value) || null}
                                options={rounderOptions}
                                onChange={(opt) =>
                                    editorProps.onUpdate(opt?.value || null)
                                }
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        )
                    };

                default:
                    return item;
            }
        });
    };

    const getClassName = (row) => {
        let currentIds = currentSelected.map((item) => item.report_type_id);
        return selectedReportID.includes(row.report_type_id) && currentIds.includes(row.report_type_id) ? 'both-selected-row' : selectedReportID.includes(row.report_type_id) ? 'selected-report-row' :
            currentIds.includes(row.report_type_id) ? 'current-selected-row' : '';
    };

    const handleTableSearch = () => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => {
            setSearchLoading(true);
            getReportMapping();
        }, 500);
    };

    const getProviderDetail = () => {
        let formData = new FormData();
        Axios.post(configURL.providers, formData).then((response) => {
            if (response.data.success === true) {
                const { aggregators = [], rounders = [] } = response.data.result || {};

                setAggregatorOptions(
                    aggregators.map(item => ({
                        label: item.name,
                        value: item.key
                    }))
                );
                setRounderOptions(
                    rounders.map(item => ({
                        label: item.name,
                        value: item.key
                    }))
                );
                setProviderLoaded(true); // ✅ THIS WAS MISSING
            } else {
                setProviderLoaded(true); // safety
            }
        }).catch(() => {
            setProviderLoaded(true); // safety
        });
    };



    useEffect(() => {
        setLoading(true);
        getProviderDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!providerLoaded) return;
        getReportMapping();
    }, [providerLoaded]);

    useEffect(() => {
        setDataColumn(buildColumns());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelected, aggregatorOptions, rounderOptions]);



    return (
        <React.Fragment>
            <section className="Page-Users">
                <div className="breadcrumb_ezi">
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            Anonymity Mapping
                        </Breadcrumb.Item>
                    </Breadcrumb>
                    <div className="column-header">
                        <h1 className="page-heading">Report type listing <span>
                            <OverlayTrigger overlay={<Tooltip>Info</Tooltip>}><FaCircleInfo size={20} style={{ cursor: 'pointer' }} onClick={() => setShowInfoModal(true)} /></OverlayTrigger></span></h1>
                        <div className="column-header-btn">
                            <button type="button" className="btn-ripple add-new" onClick={handleSaveReportData} >
                                Save & Assign
                            </button>
                        </div>
                    </div>
                </div>
                <div className="users-table-content anonymity-listing">
                    <SweetSearch
                        ref={inputSearch}
                        change={handleTableSearch}
                        loading={searchLoading}
                    />
                    {usersData.length > 0 && dataColumn.length > 0 && (
                        <div className="saved_participants_table">
                            <div className="table-scroll-wrapper">
                                <DataTable
                                    data={usersData}
                                    columns={dataColumn}
                                    hideSelectAll={true}
                                    page={pagination.current_page}
                                    sizePerPage={per_page.current}
                                    onTableChange={handleTableChange}
                                    keyField="report_type_id"
                                    totalSize={pagination.total}
                                    hideSelect={false}
                                    updateRowData={updateRowData}
                                    handleCheck={handleCheckData}
                                    // handleSelectAll={handleOnSelectAll}
                                    seletedIds={currentSelected.map((item) => item.report_type_id)}
                                    hideSizePerPage={false}
                                    getClassName={getClassName}
                                />
                            </div>
                        </div>
                    )}
                    {usersData.length === 0 && (
                        <div className="saved_participants_table">
                            <DataTable data={[]} columns={[{}]} keyField="id" hideSelect />
                        </div>
                    )}
                </div>
                {showInfoModal &&
                    <Modal show={showInfoModal} className="ezi-modal-wrapper theme-modal-wrapper survey-wise-anonymity-modal" size="lg"
                        aria-labelledby="contained-modal-title-vcenter"
                        centered>
                        <Modal.Header className="ezi-modal-header">
                            <Modal.Title id="contained-modal-title-vcenter" className="theme-modal-title ezi-modal-header-title">
                                <span className="theme-modal-title-text">Report Anonymity</span>
                                <span className="ezi-modal-close" onClick={() => { setShowInfoModal(false) }}></span>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="modal-body-content" style={{ color: '#504658', fontSize: '0.9rem' }}>
                                <ul>
                                    <li>Once a report is assigned, it will be color-coded. A color-coded report can't be unassigned, but it can be updated.</li>
                                    <li>For the Round decimal (100-scale), the default value is 0, with a valid range of 0 to 2.</li>
                                    <li>For the Round decimal (5-scale), the default value is 2, with a valid range of 0 to 2.</li>
                                </ul>
                            </div>
                        </Modal.Body>
                    </Modal>
                }
            </section>
            {loading && <EziLoader />}
        </React.Fragment>
    );
};

export default Anonymity;
